import { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNotification } from 'components/Notification'
import { createCustomerSublocation, deleteCustomerSublocation } from 'services/customerSublocations'

function normalizeSublocation(s) {
  return {
    id: s.id,
    sublocationId: s.sublocationId || s.sublocation_id,
  }
}

function useCustomerSublocations({
  customerLocationId,
  initialSublocations = [],
  onSublocationsChange,
}) {
  const [sublocations, setSublocations] = useState(() =>
    initialSublocations.map(normalizeSublocation),
  )
  const sublocationsRef = useRef(sublocations)
  const onSublocationsChangeRef = useRef(onSublocationsChange)
  const { createNotification } = useNotification()

  sublocationsRef.current = sublocations
  onSublocationsChangeRef.current = onSublocationsChange

  const { mutate: addSublocation, isLoading: isAdding } = useMutation({
    mutationFn: sublocationId => createCustomerSublocation({ customerLocationId, sublocationId }),
    onSuccess: record => {
      const next = [...sublocationsRef.current, record]
      setSublocations(next)
      onSublocationsChangeRef.current?.(next)
      createNotification({ message: 'Sublocation added', variant: 'success' })
    },
    onError: error => {
      const message =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.errors?.[0] ||
        'Failed to add sublocation'
      createNotification({ message, variant: 'error' })
    },
  })

  const { mutate: removeSublocation, isLoading: isRemoving } = useMutation({
    mutationFn: id => deleteCustomerSublocation(id),
    onSuccess: (_, id) => {
      const next = sublocationsRef.current.filter(s => s.id !== id)
      setSublocations(next)
      onSublocationsChangeRef.current?.(next)
      createNotification({ message: 'Sublocation removed', variant: 'success' })
    },
    onError: () => {
      createNotification({ message: 'Failed to remove sublocation', variant: 'error' })
    },
  })

  return { sublocations, addSublocation, removeSublocation, isAdding, isRemoving }
}

export default useCustomerSublocations
